import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WorkflowCard, isReady } from "../components/WorkflowCard";
import { useWorkflows } from "../hooks";
import { modules, moduleLabel } from "../utils";

export default function WorkflowsPage() {
  const navigate = useNavigate();

  const [moduleCode, setModuleCode] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const size = 9;

  // Always fetch all workflows (no moduleCode filter) for the coverage summary.
  const { data: allWorkflows = [], isLoading, error } = useWorkflows();

  const moduleCoverage = useMemo(() => {
    return modules.map((m) => ({
      module: m,
      ready: allWorkflows.some((w) => w.moduleCode === m && isReady(w)),
    }));
  }, [allWorkflows]);

  const data = useMemo(
    () => moduleCode ? allWorkflows.filter((w) => w.moduleCode === moduleCode) : allWorkflows,
    [allWorkflows, moduleCode],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    return data.filter((w) =>
      [w.name, w.code, w.moduleCode]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [data, search]);

  const totalPages = Math.ceil(filtered.length / size);
  const pageItems = filtered.slice(page * size, page * size + size);

  if (isLoading) return <p className="p-6">Loading workflows...</p>;

  if (error) {
    return <p className="p-6 text-red-600">Failed to load workflows.</p>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Workflows</h1>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => navigate("/admin/workflow-delegations")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold"
          >
            Delegations
          </button>

          <button
            onClick={() => navigate("/admin/workflows/new")}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
          >
            + New Workflow
          </button>
        </div>
      </div>

      {/* Module coverage — shows which modules have a ready workflow */}
      <div className="rounded-2xl border bg-white p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Module coverage
        </p>
        <div className="flex flex-wrap gap-2">
          {moduleCoverage.map(({ module, ready }) => (
            <button
              key={module}
              onClick={() => { setPage(0); setModuleCode(moduleCode === module ? "" : module); }}
              className={`rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                ready
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-amber-200 bg-amber-50 text-amber-700"
              } ${moduleCode === module ? "ring-2 ring-emerald-400" : ""}`}
            >
              {ready ? "✓" : "⚠"} {moduleLabel(module)}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 rounded-2xl border bg-white p-4 md:grid-cols-3">
        <input
          className="input"
          placeholder="Search workflow..."
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
        />

        <select
          className="input"
          value={moduleCode}
          onChange={(e) => {
            setPage(0);
            setModuleCode(e.target.value);
          }}
        >
          <option value="">All Modules</option>
          {modules.map((m) => (
            <option key={m} value={m}>
              {moduleLabel(m)}
            </option>
          ))}
        </select>

        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
          {filtered.length} workflow(s) found
        </div>
      </div>

      <div className="space-y-1 rounded-2xl border bg-white p-2">
        {pageItems.map((workflow) => (
          <WorkflowCard key={workflow.id} workflow={workflow} />
        ))}
      </div>

      {!pageItems.length ? (
        <div className="rounded-2xl border bg-white p-10 text-center text-slate-500">
          No workflows found.
        </div>
      ) : null}

      <div className="flex items-center justify-between rounded-2xl border bg-white p-4">
        <button
          disabled={page === 0}
          onClick={() => setPage((p) => p - 1)}
          className="rounded-lg border px-3 py-2 disabled:opacity-40"
        >
          Previous
        </button>

        <span className="text-sm text-slate-500">
          Page {page + 1} of {Math.max(totalPages, 1)}
        </span>

        <button
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-lg border px-3 py-2 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}